import { CORE_API_URL } from '../config';
import { authService } from './authService';

/**
 * Targeted Outreach, served by the Go core API (cmd/api).
 *
 * This replaced the legacy Python endpoints under API_BASE_URL
 * (/api/targeted-outreach/*), which read score.targeted_outreach — a table
 * whose "fit" was a candidate's own score matched to a company by name string,
 * and whose "signal" came from pattern-matching a URL that was never fetched.
 *
 * The response deliberately carries no priority score. The list arrives already
 * ranked and every item explains itself through `reasons`, so the UI never has
 * to render a number it cannot defend — the same rule Signal Sources set.
 */

function getAuthHeaders() {
  const token = authService.requireToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function request(path, init) {
  const response = await fetch(`${CORE_API_URL}${path}`, { ...init, headers: getAuthHeaders() });

  // An expired session bounces to login rather than surfacing as an error banner.
  // The access token lives 30 minutes (cmd/api -token-ttl) while this page reads
  // the user once on mount and never re-checks, so a tab left open turned into a
  // red banner over a list that could no longer be refreshed — with nothing
  // telling the user the fix was to sign in again. handleSessionExpired is
  // guarded against concurrent 401s, so parallel reads still redirect once.
  //
  // ONLY 401. A 500 is not an auth problem, and logging someone out because a
  // server had a bad minute would destroy their session for no reason.
  if (response.status === 401) {
    authService.handleSessionExpired();
    throw new Error('Session expired — please sign in again.');
  }

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      // cmd/api's writeErr answers {"detail": "..."} — the one error contract the
      // Python API and every other Go route share. Reading `error` here threw the
      // server's message away and left the user with a bare status code.
      const body = await response.json();
      if (body?.detail) message = body.detail;
    } catch {
      // A non-JSON error body tells us less than the status already has.
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

/**
 * A write. Undefined fields are dropped rather than serialised as `null`:
 * `{"until": null}` and an absent `until` mean different things to the API — the
 * first is a value the caller chose, the second is "use the default" — and
 * sending null for an unset optional would turn every default into a 400.
 */
async function post(path, body) {
  const payload = Object.fromEntries(
    Object.entries(body || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  return request(path, { method: 'POST', body: JSON.stringify(payload) });
}

export const targetedOutreachService = {
  /**
   * One bucket of the ranked list. The bucket is a filter rather than a page:
   * "call now" and "warm" are different claims about urgency and should not be
   * mixed into a single scroll.
   *
   * dims picks which desk dimensions apply: both (send nothing) | territory | sector | none.
  // Sending nothing is deliberate -- the server defaults to the WHOLE desk, so a
  // caller that forgets cannot silently turn scoping off
   * (Task 5). Omitting it is what makes the server apply the desk by default,
   * so it is only ever added when true -- never sent as an explicit "apply".
   */
  async getList({ bucket = 'call_now', limit = 50, dims = 'both', search = '' } = {}) {
    const params = new URLSearchParams();
    if (bucket) params.set('bucket', bucket);
    if (limit) params.set('limit', String(limit));
    if (dims !== 'both') params.set('scope', dims === 'none' ? 'all' : dims);
    // Server-side: the box must reach the WHOLE scoped list, not just the rows
    // already fetched. Filtering client-side made a search for the 101st
    // company come back empty.
    if (search) params.set('search', search);
    return request(`/api/outreach/list?${params.toString()}`);
  },

  /**
   * The drawer: the item plus the evidence behind it — external observations
   * and the people who moved from your talent pool onto their side.
   *
   * A 404 means the company has no evidence at all. That is a real answer, not
   * a failure, and callers should say so plainly.
   */
  async getCompany(companyId) {
    return request(`/api/outreach/companies/${companyId}`);
  },

  /**
   * The movements popup: signal events and the score moves they caused, as ONE
   * date-ordered timeline.
   *
   * Merged and ordered SERVER-side on purpose. Interleaving two arrays here
   * would put the ordering rule -- signals fall back to first_seen_at when
   * undated, moves order by changed_at -- in a place no API test can reach, and
   * it is a rule about data, not about presentation.
   *
   * An empty `movements` is a real answer: the company exists and nothing has
   * happened. Only a company that is not yours 404s.
   */
  async getMovements(companyId) {
    return request(`/api/outreach/companies/${companyId}/movements`);
  },

  /**
   * The four verbs the page was missing. Until these landed a recruiter could be
   * told who to call and had nowhere to record that they had.
   *
   * None of them writes a score. The legacy Python endpoints these replace set
   * `bucket = 'suppressed'` and clamped `priority_score` on the scorer's own row,
   * so a click overwrote a model's answer and the "add back" path had to
   * reconstruct the value it had destroyed. Here every verb appends a fact and
   * the list applies it when it reads.
   */
  async logAction(companyId, { action, note } = {}) {
    return post(`/api/outreach/companies/${companyId}/actions`, { action, note });
  },

  /** Omitting `until` gets the legacy page's actual button: seven days. */
  async snooze(companyId, { until, note } = {}) {
    return post(`/api/outreach/companies/${companyId}/snooze`, { until, note });
  },

  /**
   * Undo a snooze or a dismissal. The company returns to the bucket the MODEL
   * gave it, because nothing ever overwrote it.
   */
  async reopen(companyId) {
    return post(`/api/outreach/companies/${companyId}/reopen`, {});
  },

  async markNotRelevant(companyId, { reason } = {}) {
    return post(`/api/outreach/companies/${companyId}/not-relevant`, { reason });
  },
};
